import type { SportsOutcome } from "@townsy/contracts";

/** The big line over the court after your shot, kick, point or match (founder 2026-10-14: "Missed Shot" / "Shot Made"). */
export interface OutcomeBannerView {
  title: string;
  detail: string | null;
  tone: "good" | "bad";
}

/** How long the banner stays up. */
export const OUTCOME_BANNER_MS = 2600;

/**
 * What the local player sees for a server-decided outcome, or null when it is not theirs (someone else's shot shows in
 * the notices only). A tennis match result names the winner in `by` and the loser in `against`.
 */
export function outcomeBanner(o: SportsOutcome, me: string | null): OutcomeBannerView | null {
  if (!me) return null;
  const score = o.score ? `${o.score[0]}-${o.score[1]}` : null;
  if (o.outcome === "match_won") {
    if (o.by === me) return { title: "You won the match!", detail: score, tone: "good" };
    if (o.against === me) return { title: "Match lost", detail: o.score ? `${o.score[1]}-${o.score[0]}` : null, tone: "bad" };
    return null;
  }
  if (o.by !== me) return null;
  const join = (...parts: Array<string | null | undefined>) => parts.filter(Boolean).join(" · ") || null;
  switch (o.outcome) {
    case "made":
      return { title: "Shot made!", detail: join(o.points ? `+${o.points} points` : null, o.detail), tone: "good" };
    case "missed":
      return { title: "Missed shot", detail: o.detail ?? null, tone: "bad" };
    case "goal":
      return { title: "Goal!", detail: o.detail ?? null, tone: "good" };
    case "saved":
      return { title: "Saved!", detail: o.detail ?? null, tone: "bad" };
    case "wide":
    case "over":
      return { title: "Missed", detail: o.detail ?? null, tone: "bad" };
    case "out":
      return { title: "Out of play", detail: null, tone: "bad" };
    case "point_won":
      return { title: "Point won", detail: join(o.detail, score), tone: "good" };
    case "point_lost":
      return { title: "Point lost", detail: join(o.detail, score), tone: "bad" };
  }
}
